import React, { useEffect, useState } from 'react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../utlis/axiosInstance';
import { useUser } from '../provider/UserProvider';
import styles from './PointsTransfer.module.css';

// Use the same User interface as in UserManagement
interface User {
  id: number;
  name: string;
  email: string;
  password: string;
  readonly: boolean;
  phone: string;
  disabled: boolean;
  map: string;
  coordinate_format: string;
  device_readonly: boolean;
  administrator: boolean;
  latitude: number;
  longitude: number;
  zoom: number;
  device_limit: number;
  user_limit: number;
  expiration_time: string;
  limit_commands: boolean;
  fixed_email: boolean;
  poi_layer: string;
  attributes: Record<string, any>;
  city: string;
  state: string;
  pincode: string;
  points: number;
  current_plan: number | null;
  plan_purchased_at: string | null;
  plan_expires_at: string | null;
}

const PointsTransfer: React.FC = () => {
  const { permissions: userPermissions, user } = useUser();
  
  // Check if user is admin
  const isAdmin = user?.user?.administrator;
  
  const [points, setPoints] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userPoints, setUserPoints] = useState<number>(0);
  const [userPointsLoading, setUserPointsLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Get logged-in user from localStorage
  const loggedInUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  })();

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get('/gpstracker/userhandler/');
        if (response.data && response.data.status && response.data.responseData) {
          setUsers(response.data.responseData);
        } else {
          setError('Failed to fetch users');
        }
      } catch (err) {
        setError('Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const getUserPoints = async () => {
      if (!loggedInUser?.id) return;
      setUserPointsLoading(true);
      try {
        const response = await axiosInstance.get(`/gpstracker/userhandler/${loggedInUser.id}`);
        if (response.data?.status && response.data) {
          let responseData;
          if (typeof response.data === 'string') {
            try {
              const sanitizedData = response.data.replace(/"points":\s*Infinity/g, '"points": 999999999');
              responseData = JSON.parse(sanitizedData);
            } catch (parseError) {
              setUserPoints(0);
              setUserPointsLoading(false);
              return;
            }
          } else {
            responseData = response.data;
          }
          const points = responseData.responseData?.points;
          if (typeof points === 'number' && isFinite(points)) {
            setUserPoints(points);
          } else {
            setUserPoints(0);
          }
        }
      } catch (error) {
        setUserPoints(0);
      } finally {
        setUserPointsLoading(false);
      }
    };
    getUserPoints();
  }, [loggedInUser?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    if (!points || !selectedUser) {
      setError('Please enter points and select a user.');
      return;
    }
    const pointsToAdd = Number(points);
    if (isNaN(pointsToAdd) || pointsToAdd <= 0) {
      setError('Please enter a valid number of points to transfer.');
      return;
    }
    setTransferLoading(true);
    try {
      // Fetch the latest user object
      const userRes = await axiosInstance.get(`/gpstracker/userhandler/${selectedUser.id}`);
      let userObj = userRes.data?.responseData;
      if (!userObj) {
        setError('Failed to fetch user details.');
        setTransferLoading(false);
        return;
      }
      // Add points
      const newPoints = (userObj.points || 0) + pointsToAdd;
      const updatedUser = { ...userObj, points: newPoints };
      delete updatedUser.can_create_device
      // Send PUT request to update user
      await axiosInstance.put(`/gpstracker/userhandler/${selectedUser.id}`, updatedUser);
      setSuccess('Points transferred successfully!');
      setPoints('');
      setSelectedUser(null);
      setSearchTerm('');
    } catch (err) {
      setError('Failed to transfer points. Please try again.');
    } finally {
      setTransferLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setSearchTerm(`${user.name} (${user.email})`);
    setShowDropdown(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>Points Transfer</h1>
        </div>

        {/* Stats Grid */}
        <div className={styles.statsGrid}>
          {/* Current Points Card */}
          <div className={styles.pointsCard}>
            <div className={styles.pointsAvatar}>
              <EmojiEventsIcon sx={{ fontSize: 32, color: 'white' }} />
            </div>
            <div className={styles.pointsInfo}>
              <p className={styles.pointsLabel}>Your Available Points</p>
              <div className={styles.pointsValue}>
                {isAdmin ? (
                  <>
                    <span className={styles.infinitePoints}>∞</span>
                    <span className={styles.infiniteText}>Infinite Points</span>
                  </>
                ) : (
                  <>
                    {userPoints >= 0 ? userPoints.toLocaleString() : 0} Points
                    {userPointsLoading && <div className={styles.loadingSpinner} />}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Transfer Section */}
        <div className={styles.transferSection}>
          <div className={styles.transferHeader}>
            <h2 className={styles.transferTitle}>Transfer Points</h2>
            <p className={styles.transferSubtitle}>
              {isAdmin 
                ? 'Transfer unlimited points to any user in your organization' 
                : 'Transfer points to another user in your organization'
              }
            </p>
          </div>

          <form onSubmit={handleSubmit} className={styles.transferForm}>
            {/* Points Input */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Points to Transfer</label>
              <input
                type="number"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className={styles.formInput}
                placeholder={isAdmin ? "Enter number of points (unlimited)" : "Enter number of points"}
                required
                min="1"
                disabled={isAdmin ? false : userPoints <= 0}
              />
              {isAdmin && (
                <p className={styles.adminNote}>Admin: You can transfer unlimited points</p>
              )}
            </div>

            {/* User Selection */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Select User</label>
              <div className={styles.autocompleteContainer}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                    if (!e.target.value) {
                      setSelectedUser(null);
                    }
                  }}
                  onFocus={() => setShowDropdown(true)}
                  className={styles.autocompleteInput}
                  placeholder="Search user by name or email"
                  required
                />
                {showDropdown && searchTerm && (
                  <div className={styles.autocompleteDropdown}>
                    {loading ? (
                      <div className={styles.autocompleteOption}>
                        <div className={styles.loadingSpinner} />
                        Loading users...
                      </div>
                    ) : filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <div
                          key={user.id}
                          className={styles.autocompleteOption}
                          onClick={() => handleUserSelect(user)}
                        >
                          {user.name} ({user.email})
                        </div>
                      ))
                    ) : (
                      <div className={styles.autocompleteOption}>
                        No users found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Error Alert */}
            {error && (
              <div className={`${styles.alert} ${styles.error}`}>
                <WarningIcon sx={{ fontSize: 18 }} />
                {error}
              </div>
            )}

            {/* Success Alert */}
            {success && (
              <div className={`${styles.alert} ${styles.success}`}>
                <CheckCircleIcon sx={{ fontSize: 18 }} />
                {success}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className={`${styles.submitButton} ${transferLoading ? styles.loading : ''}`}
              disabled={loading || transferLoading || (!isAdmin && userPoints <= 0)}
            >
              {transferLoading ? (
                <>
                  <div className={styles.loadingSpinner} />
                  Transferring...
                </>
              ) : (
                'Transfer Points'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PointsTransfer; 